import { useState, useCallback, useEffect } from 'react';

export type WebRTCProtocol = 'legacy' | 'whep' | 'auto';

const STORAGE_KEY = 'lumeo.webrtc.protocol';

/**
 * Hook to manage WebRTC protocol preference.
 *
 * The protocol can be switched via browser console:
 *   localStorage.setItem('lumeo.webrtc.protocol', 'whep')
 *   localStorage.setItem('lumeo.webrtc.protocol', 'legacy')
 *   localStorage.setItem('lumeo.webrtc.protocol', 'auto')
 *
 * Or using the helper functions exposed on window:
 *   window.lumeo.useWhep()
 *   window.lumeo.useLegacyWebRTC()
 *   window.lumeo.useAutoWebRTC()
 *   window.lumeo.getWebRTCProtocol()
 *
 * 'auto' mode (default) tries WHEP first and falls back to legacy
 * if the api-server returns a 'whep-unsupported' error (gateway < 2.6.0).
 */
const DEFAULT_PROTOCOL: WebRTCProtocol = 'auto';

export function useWebRTCProtocol() {
  const [protocol, setProtocolState] = useState<WebRTCProtocol>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'whep' || stored === 'legacy' || stored === 'auto') {
        return stored;
      }
    } catch {
      // localStorage not available
    }
    return DEFAULT_PROTOCOL;
  });

  const setProtocol = useCallback((newProtocol: WebRTCProtocol) => {
    try {
      localStorage.setItem(STORAGE_KEY, newProtocol);
    } catch {
      // localStorage not available
    }
    setProtocolState(newProtocol);
  }, []);

  // Listen for storage changes (e.g., from another tab or console)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        const newValue = event.newValue;
        if (newValue === 'whep' || newValue === 'legacy' || newValue === 'auto') {
          setProtocolState(newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return { protocol, setProtocol };
}

/**
 * Get the current WebRTC protocol from localStorage (for use outside React).
 */
export function getWebRTCProtocol(): WebRTCProtocol {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'whep' || stored === 'legacy' || stored === 'auto') {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_PROTOCOL;
}

/**
 * Set the WebRTC protocol in localStorage (for use outside React).
 */
export function setWebRTCProtocol(protocol: WebRTCProtocol): void {
  try {
    localStorage.setItem(STORAGE_KEY, protocol);
    // Dispatch a storage event so React components update
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: STORAGE_KEY,
        newValue: protocol,
      })
    );
  } catch {
    // localStorage not available
  }
}

// Expose helper functions on window for easy console access
declare global {
  interface Window {
    lumeo?: {
      useWhep: () => void;
      useLegacyWebRTC: () => void;
      useAutoWebRTC: () => void;
      getWebRTCProtocol: () => WebRTCProtocol;
    };
  }
}

if (typeof window !== 'undefined') {
  window.lumeo = {
    ...window.lumeo,
    useWhep: () => {
      setWebRTCProtocol('whep');
      console.log(
        'WebRTC protocol set to WHEP (forced). Refresh the page to apply.'
      );
    },
    useLegacyWebRTC: () => {
      setWebRTCProtocol('legacy');
      console.log(
        'WebRTC protocol set to legacy (forced). Refresh the page to apply.'
      );
    },
    useAutoWebRTC: () => {
      setWebRTCProtocol('auto');
      console.log(
        'WebRTC protocol set to auto. Refresh the page to apply.'
      );
    },
    getWebRTCProtocol: () => {
      const protocol = getWebRTCProtocol();
      console.log(`Current WebRTC protocol: ${protocol}`);
      return protocol;
    },
  };
}
